<template>
    <div :class="iconWrapperMergedClasses" v-if="type">
        <component :is="iconComponent" :filled="props.filled" :class="iconClasses" />
    </div>
</template>

<script lang="ts" setup>
    import { ref, defineAsyncComponent, watch, markRaw, computed } from 'vue'
    import { iconComponents, IconType } from '../icons/iconConfig'
    import { twMerge } from 'tailwind-merge'

    const props = withDefaults(
        defineProps<{
            class?: any
            type?: IconType | undefined
            filled?: boolean
            strokeWeight?: 'regular' | 'medium' | 'semibold' | 'bold'
        }>(),
        {
            filled: false,
            strokeWeight: 'regular'
        }
    )

    // Use a ref to store the raw component definition
    const iconComponent = ref<ReturnType<typeof defineAsyncComponent>>()

    // Update the icon component only when necessary
    watch(
        () => props.type,
        (newType) => {
            if (newType && iconComponents[newType]) {
                iconComponent.value = markRaw(defineAsyncComponent(iconComponents[newType]))
            } else {
                //console.warn(`Invalid icon type: ${newType}. Defaulting to 'fallback'.`)
                // Handle fallback logic here if necessary
            }
        },
        { immediate: true }
    )

    const iconClasses = computed(() => {
        const strokeWeightOptions = {
            regular: '[&>svg.outlined]:[stroke-width:1]',
            medium: '[&>svg.outlined]:[stroke-width:1.25]',
            semibold: '[&>svg.outlined]:[stroke-width:1.5]',
            bold: '[&>svg.outlined]:[stroke-width:1.75]'
        }

        const baseClasses =
            'animate-appear [&>svg]:w-full [&>svg]:h-full [&>svg.filled_*]:[fill:currentColor] [&>svg.outlined_*]:[stroke:currentColor] [&>svg.outlined]:[overflow:visible] [&_*]:[vector-effect:non-scaling-stroke] [&_*]:[stroke-linecap:round] [&_*]:[stroke-linejoin:round]'

        return [baseClasses, strokeWeightOptions[props.strokeWeight]]
    })

    const iconWrapperBaseClasses = computed(() => {
        return ['block aspect-square shrink-0 w-full']
    })

    // Allow overriding base classes
    const iconWrapperMergedClasses = computed(() => twMerge(iconWrapperBaseClasses.value, props.class))
</script>
